<script setup>
import { useLayout } from '@/components/layouts/composables/layout';
import { Menu } from 'primevue';
import { computed, ref } from "vue";
import { useRouter } from 'vue-router';
import { useAuth } from '@/composables/useAuth';
import { useRoles } from '@/composables/useRoles';
import { useSelectedBrand } from '@/composables/useSelectedBrand';
import CompanyLogo from '../CompanyLogo.vue';
import { useEnvironment } from '@/composables/useEnvironment';

const { toggleMenu, toggleDarkMode, isDarkTheme } = useLayout();
const { isProduction, envLabel } = useEnvironment();
const router = useRouter();
const { currentUser } = useAuth();
const { currentGroupLabel } = useRoles();

// Active brand (#2122): shown in the popover; switching happens on the dedicated
// brand page reached via the "Switch Brand" item below.
const { selectedBrand } = useSelectedBrand();

const userName = computed(() => currentUser.value?.name || currentUser.value?.email || 'Admin');
const userEmail = computed(() => currentUser.value?.email || '');
const userInitials = computed(() => {
    const name = (currentUser.value?.name || '').trim();
    if (name) {
        const parts = name.split(/\s+/);
        return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || 'A';
    }
    return (currentUser.value?.email?.[0] || 'A').toUpperCase();
});

const toggle = (event) => {
    menu.value.toggle(event);
};

const handleLogout = () => {
    router.push({ name: 'logout' });
};

const menu = ref();
const items = ref([
    {
        label: 'Switch Brand',
        icon: 'pi pi-building',
        link: { name: 'selectBrand' }
    },
    {
        label: 'Settings',
        icon: 'pi pi-cog',
        link: { name: 'SettingsView' }
    },
    { separator: true },
    {
        label: 'Logout',
        icon: 'pi pi-sign-out',
        command: handleLogout,
        danger: true
    }
]);
</script>

<template>
    <div class="topbar-gradient-wrapper shadow-sm">
        <div class="layout-topbar">
            <div class="absolute w-full bottom-0 left-0"
                style="padding: 0.1rem; background: linear-gradient(90deg, var(--primary-color) 0%, var(--primary-color) 50%, transparent 100%);">
            </div>

            <div class="layout-topbar-logo-container">
                <button class="layout-menu-button layout-topbar-action" @click="toggleMenu">
                    <i class="pi pi-bars"></i>
                </button>
                <router-link to="/" class="layout-topbar-logo">
                    <CompanyLogo />
                </router-link>
                <span v-if="!isProduction"
                    class="text-xs font-bold select-none self-end mb-1 ml-2 text-text-secondary">
                    {{ envLabel }}
                </span>
            </div>

            <div class="layout-topbar-actions">
                <div class="layout-config-menu">
                    <button type="button" class="layout-topbar-action" @click="toggleDarkMode">
                        <i :class="['pi', { 'pi-moon': isDarkTheme, 'pi-sun': !isDarkTheme }]"></i>
                    </button>
                    <div class="flex items-center">
                        <button type="button" class="layout-topbar-action profile-trigger" @click="toggle"
                            aria-haspopup="true" aria-controls="overlay_menu" aria-label="Open profile menu">
                            <!-- div, not span: .layout-topbar-action span { display:none } hides label spans -->
                            <div class="profile-trigger-avatar">{{ userInitials }}</div>
                        </button>
                        <Menu ref="menu" id="overlay_menu" class="profile-menu" :model="items" :popup="true">
                            <template #start>
                                <div class="pm-head">
                                    <div class="pm-avatar">{{ userInitials }}</div>
                                    <div class="pm-id">
                                        <div class="pm-name">{{ userName }}</div>
                                        <div v-if="userEmail && userEmail !== userName" class="pm-email">
                                            {{ userEmail }}
                                        </div>
                                    </div>
                                </div>
                                <div class="pm-sub">
                                    <span class="pm-role">{{ currentGroupLabel }}</span>
                                </div>
                                <div class="pm-brand">
                                    <span class="pm-brand-label">Active brand</span>
                                    <div class="pm-brand-logo">
                                        <CompanyLogo :brand-code="selectedBrand.code"
                                            :brand-name="selectedBrand.name" />
                                    </div>
                                </div>
                            </template>
                            <template #item="{ item }">
                                <router-link v-if="item.link" :to="item.link" class="pm-item">
                                    <span class="pm-item-ico"><i :class="item.icon"></i></span>
                                    <span class="pm-item-label">{{ item.label }}</span>
                                    <i class="pi pi-angle-right pm-item-chev"></i>
                                </router-link>
                                <div v-else-if="item.command" class="pm-item"
                                    :class="{ 'pm-item-danger': item.danger }" @click="item.command">
                                    <span class="pm-item-ico"><i :class="item.icon"></i></span>
                                    <span class="pm-item-label">{{ item.label }}</span>
                                </div>
                                <div v-else class="pm-item">
                                    <span class="pm-item-ico"><i :class="item.icon"></i></span>
                                    <span class="pm-item-label">{{ item.label }}</span>
                                </div>
                            </template>
                        </Menu>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<!-- Global (unscoped): the PrimeVue Menu popup is teleported to <body>, so scoped
     styles can't reach it. Namespaced under .profile-menu so nothing else is hit. -->
<style>
.profile-trigger .profile-trigger-avatar {
    display: grid;
    place-items: center;
    width: 30px;
    height: 30px;
    border-radius: 9px;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    color: var(--primary-color);
    background: color-mix(in srgb, var(--primary-color) 16%, transparent);
    border: 1px solid color-mix(in srgb, var(--primary-color) 30%, transparent);
}

.profile-menu.p-menu {
    width: 17.5rem;
    padding: 0.5rem;
    border-radius: var(--khk-radius-lg);
    border: 1px solid var(--surface-border);
    box-shadow: var(--khk-shadow-lg);
}

/* Identity header */
.profile-menu .pm-head {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    padding: 0.55rem 0.55rem 0.5rem;
}

.profile-menu .pm-avatar {
    flex: none;
    width: 44px;
    height: 44px;
    border-radius: 13px;
    display: grid;
    place-items: center;
    font-weight: 700;
    font-size: 1.05rem;
    color: var(--primary-color);
    background: color-mix(in srgb, var(--primary-color) 16%, transparent);
    border: 1px solid color-mix(in srgb, var(--primary-color) 28%, transparent);
}

.profile-menu .pm-id {
    min-width: 0;
}

.profile-menu .pm-name {
    font-weight: 600;
    font-size: 0.92rem;
    color: var(--text-color);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.profile-menu .pm-email {
    font-size: 0.78rem;
    color: var(--text-color-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.profile-menu .pm-sub {
    padding: 0 0.55rem 0.6rem;
}

.profile-menu .pm-role {
    display: inline-block;
    font-size: 0.66rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--text-color-secondary);
    background: var(--surface-hover);
    border-radius: 999px;
    padding: 0.2rem 0.6rem;
}

/* Active brand highlight: the real brand logo, labelled. */
.profile-menu .pm-brand {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    margin: 0 0.2rem 0.35rem;
    padding: 0.65rem 0.7rem;
    border-radius: var(--khk-radius-md);
    background: color-mix(in srgb, var(--primary-color) 8%, transparent);
    border: 1px solid color-mix(in srgb, var(--primary-color) 18%, transparent);
}

.profile-menu .pm-brand-label {
    font-size: 0.64rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-color-secondary);
}

.profile-menu .pm-brand-logo {
    display: flex;
    align-items: center;
    height: 28px;
    overflow: hidden;
}

/* CompanyLogo renders an SVG (.company-logo) or a wordmark fallback
   (.brand-wordmark); size both to sit in the labelled slot. Higher specificity
   than CompanyLogo's own scoped rules so it wins across the component boundary. */
.profile-menu .pm-brand-logo .company-logo {
    height: 28px;
    max-height: 28px;
    width: auto;
}

.profile-menu .pm-brand-logo .brand-wordmark {
    font-size: 1.05rem;
}

/* Menu rows: neutralize PrimeVue's default item chrome, use our own row.
   Neutralizing .p-focus here drops PrimeVue's keyboard-focus highlight, so every
   hover rule below is paired with a .p-focus selector that re-applies it to
   .pm-item; otherwise arrowing through the menu would give no visual feedback.
   PrimeVue puts .p-focus on the li (menu/style: 'p-focus': id === focusedOptionId)
   and drives focus with aria-activedescendant, so :focus-visible on our row would
   never fire, so .p-focus is the only usable hook. */
.profile-menu .p-menu-list {
    padding: 0;
}

.profile-menu .p-menu-item-content {
    padding: 0;
    border-radius: 10px;
    background: transparent;
    transition: none;
}

.profile-menu .p-menu-item-content:hover,
.profile-menu .p-menu-item.p-focus > .p-menu-item-content {
    background: transparent;
    color: inherit;
}

.profile-menu .pm-item {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    width: 100%;
    padding: 0.5rem 0.55rem;
    border-radius: 10px;
    color: var(--text-color);
    text-decoration: none;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
}

.profile-menu .pm-item:hover,
.profile-menu .p-focus > .p-menu-item-content .pm-item {
    background: var(--surface-hover);
}

/* Keyboard focus additionally gets a ring: hover has a pointer to signal intent,
   arrow-key navigation has nothing. */
.profile-menu .p-focus > .p-menu-item-content .pm-item {
    outline: 2px solid var(--primary-color);
    outline-offset: -2px;
}

.profile-menu .pm-item-ico {
    flex: none;
    width: 30px;
    height: 30px;
    border-radius: 9px;
    display: grid;
    place-items: center;
    font-size: 0.85rem;
    color: var(--text-color-secondary);
    background: var(--surface-hover);
    transition: background 0.15s ease, color 0.15s ease;
}

.profile-menu .pm-item:hover .pm-item-ico,
.profile-menu .p-focus > .p-menu-item-content .pm-item-ico {
    color: var(--primary-color);
    background: color-mix(in srgb, var(--primary-color) 15%, transparent);
}

.profile-menu .pm-item-label {
    flex: 1;
    font-size: 0.88rem;
    font-weight: 500;
}

.profile-menu .pm-item-chev {
    font-size: 0.75rem;
    color: var(--text-color-secondary);
    opacity: 0;
    transform: translateX(-4px);
    transition: opacity 0.15s ease, transform 0.15s ease;
}

.profile-menu .pm-item:hover .pm-item-chev,
.profile-menu .p-focus > .p-menu-item-content .pm-item-chev {
    opacity: 1;
    transform: translateX(0);
}

.profile-menu .p-menu-separator {
    border-top: 1px solid var(--surface-border);
    margin: 0.35rem 0.3rem;
}

/* Logout: subtle danger affordance on hover / keyboard focus. */
.profile-menu .pm-item-danger:hover,
.profile-menu .p-focus > .p-menu-item-content .pm-item-danger {
    background: color-mix(in srgb, var(--severity-error) 12%, transparent);
    color: var(--severity-error);
    outline-color: var(--severity-error);
}

.profile-menu .pm-item-danger:hover .pm-item-ico,
.profile-menu .p-focus > .p-menu-item-content .pm-item-danger .pm-item-ico {
    color: var(--severity-error);
    background: color-mix(in srgb, var(--severity-error) 15%, transparent);
}
</style>
